import { VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    applyMatchCutOperation,
    toggleSceneMorphEnabledOperation,
} from 'js/src/luminary/luminary-operations';
import {
    hasAppliedMatchCut,
    hasAppliedMatchCutWithNextScene,
    hasAppliedTransition,
} from 'js/src/luminary/luminary-selectors';
import type { MorphDisqualifiedReason } from 'js/src/luminary/morph-recommendation';

const REFUSAL_BY_REASON: Record<MorphDisqualifiedReason, string> = {
    'no-morphable-assets':
        'one of them has nothing a match cut can carry across where they meet, though it may hold other content such as a chart or fancy captions',
    'incompatible-structure':
        'one of them has no text where they meet, and text is what a match cut carries across',
    'below-threshold':
        'their text does not line up: either the two scenes hold different numbers of text blocks, or the text sits in nearly the same place in both. A match cut reads best when matching text blocks sit in noticeably different positions',
    'shares-morphable-content':
        'they already share content that carries across on its own, such as the same visual element or identical text, so a match cut would have nothing to add',
    'consecutive-morph-limit':
        'the scene on the far side of one of them already match cuts, and a match cut cannot chain across three or more scenes',
    'adjacent-foreign-morph':
        'a neighbouring gap is already animated by another feature, such as an animated zoom, and a match cut cannot chain onto it',
    'scene-sequence':
        'one of them belongs to a pre-built scene sequence, which already animates between its scenes as it was designed to',
};

export const setMatchCutSchema = z.object({
    scene_id: z
        .string()
        .describe(
            'The scene_id, from the video state, of the FIRST of the two scenes. The match cut plays where it meets the scene that follows it.',
        ),
    enabled: z
        .boolean()
        .describe(
            'true to match cut this scene into the next one, false to remove a match cut already there.',
        ),
});
export type SetMatchCutArgs = z.infer<typeof setMatchCutSchema>;

export class SetMatchCutTool extends VideoStateTool<typeof setMatchCutSchema> {
    protected override readonly schema = setMatchCutSchema;
    protected override readonly toolName = TOOL_NAMES.setMatchCut;
    protected override readonly toolDescription =
        'Match-cuts a scene into the one after it, making two scenes transition dynamically. Texts that pair up across the two scenes slide from where they sit in the first to where they sit in the second instead of cutting. Use it on a gap where the two scenes carry one connected idea, so the movement means something: a question and its answer, a problem and its fix, a before and an after, a claim and its evidence. It takes the gap over from the transition the scene design chose, so do not spend it on two scenes that merely sit next to each other, and leave it off a scene that has to look rigid or needs a particular transition. A scene pair it would not suit is auto refused by the algorithm, and it will give you a reason saying what to change';

    public override getToolDisplayName(): string {
        return 'Setting match cut';
    }

    public get statusIcon(): string {
        return 'transition_chop';
    }

    public async execute(args: SetMatchCutArgs, context: ToolExecutionContext) {
        const { enabled } = args;
        const { parentActionGroup } = context;

        try {
            const { scene, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            if (scene.isPrePackagedMorphWithNextScene) {
                return `Error: The transition between scene ${sceneShortId} and the scene after it cannot be changed because ${REFUSAL_BY_REASON['scene-sequence']}.`;
            }

            const { applyMatchCut, toggleSceneMorphEnabled } = makeOperationsCallable(
                { applyMatchCutOperation, toggleSceneMorphEnabledOperation },
                this.luminary,
                this.dispatch,
                () => this.getState(),
            );

            if (!enabled) {
                if (hasAppliedMatchCut(scene)) {
                    toggleSceneMorphEnabled(scene.id, false, parentActionGroup);
                    return `Success: Removed the match cut on scene ${sceneShortId}.`;
                }
                if (!scene.nextScene) {
                    return `Error: Scene ${sceneShortId} is the last scene in the video, so there is no gap after it to carry a match cut.`;
                }
                if (scene.morphEnabled) {
                    return `Error: The gap after scene ${sceneShortId} does animate, but not with a match cut: the movement comes from another feature, such as an animated zoom. Leave it alone; removing it here would break that feature.`;
                }
                const parkedNote =
                    scene.matchCutTechnique !== VideoData.MATCH_CUT_NONE &&
                    !this.luminary.template.transitionsEnabled
                        ? ' A match cut is still saved on this gap and comes back if the user switches "Scene transitions" on again in the Style tab.'
                        : '';
                return `Success: There is no match cut between scene ${sceneShortId} and the scene after it.${parkedNote}`;
            }

            const nextScene = scene.nextScene;
            if (!nextScene) {
                return `Error: Scene ${sceneShortId} is the last scene in the video, so there is nothing after it to match cut to. Pass the earlier of the two scenes you want to join.`;
            }
            if (!this.luminary.template.transitionsEnabled) {
                return `Error: Scene transitions are turned off for this whole video, so a match cut set now would not survive a reload. Ask the user to switch "Scene transitions" back on in the Style tab first.`;
            }
            if (hasAppliedMatchCutWithNextScene(scene)) {
                return `Success: There is already a match cut between scene ${sceneShortId} and the scene after it.`;
            }
            if (scene.morphEnabled) {
                return `Error: The gap after scene ${sceneShortId} already animates through another feature, such as an animated zoom, and a match cut cannot share a gap with it.`;
            }

            const replacedTransition = hasAppliedTransition(nextScene);
            const recommendation = applyMatchCut(scene.id, false, parentActionGroup);
            if (!recommendation) {
                throw new Error(`No scene pair to match cut for scene ${sceneShortId}`);
            }
            if (!recommendation.recommended) {
                return `Error: Scene ${sceneShortId} cannot match cut into the scene after it because ${REFUSAL_BY_REASON[recommendation.disqualifiedReason]}.`;
            }

            const replacedTransitionNote = replacedTransition
                ? ' The transition that was on that gap is gone, and removing the match cut will not bring it back.'
                : '';
            return `Success: Added a match cut between scene ${sceneShortId} and the scene after it.${replacedTransitionNote}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting the match cut: ${errorMessage}`;
        }
    }
}
